import { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    LinearProgress,
} from '@mui/material';
import UseToast from '../UseToast';
import { uploadFileInChunks, shouldUseChunkedUpload } from '../../utils/chunkedUpload';

/** Human-readable file size */
const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const BulkCaptionUpload = ({ userId, buttonColor, currentLanguage }) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPhase, setUploadPhase] = useState(''); // 'uploading' | 'processing'
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);

    const { showMsgSuccessToast, showErrorToast } = UseToast();

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);

    useEffect(() => {
        if (userId) fetchLocations();
    }, [userId]);

    const fetchLocations = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/wayfinder/get-locations/${userId}`);
            if (response.data.success) {
                setLocations(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            showErrorToast('Failed to load locations');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.name.endsWith('.zip')) {
                setSelectedFile(file);
                setUploadResult(null);
            } else {
                showErrorToast('Please select a ZIP file');
                event.target.value = '';
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            showErrorToast('Please select a ZIP file first');
            return;
        }

        if (!userId) {
            showErrorToast('User ID is missing. Please refresh the page.');
            return;
        }

        try {
            setUploading(true);
            setUploadPhase('uploading');
            setUploadProgress(0);
            setUploadResult(null);
            setJobId(null);

            // Check if file is large and needs chunked upload
            const useChunked = shouldUseChunkedUpload(selectedFile.size);
            
            if (useChunked) {
                console.log(`📦 File is large (${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB), using chunked upload`);
                
                // Use chunked upload
                const result = await uploadFileInChunks(
                    selectedFile,
                    userId,
                    'caption',
                    (progress) => setUploadProgress(progress),
                    (phase) => {
                        if (phase === 'uploading') setUploadPhase('uploading');
                        else if (phase === 'combining') setUploadPhase('processing');
                        else if (phase === 'processing') setUploadPhase('processing');
                    }
                );

                if (result.success && result.jobId) {
                    setJobId(result.jobId);
                    setUploadProgress(0);
                    startPolling(result.jobId);
                } else {
                    showErrorToast(result.message || 'Upload failed');
                    setUploading(false);
                    setUploadPhase('');
                }
            } else {
                console.log(`📦 File is small (${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB), using direct upload`);
                
                // Use direct upload for smaller files
                const formData = new FormData();
                formData.append('captionZip', selectedFile);
                formData.append('userId', userId);

                const response = await axios.post(
                    '/api/locations/bulk-caption-upload',
                    formData,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        timeout: 120000, // 2 minutes for upload
                        maxBodyLength: Infinity,
                        maxContentLength: Infinity,
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(percentCompleted);
                        },
                    }
                );

                if (response.data.success && response.data.jobId) {
                    const newJobId = response.data.jobId;
                    setJobId(newJobId);
                    setUploadPhase('processing');
                    setUploadProgress(0);
                    startPolling(newJobId);
                } else {
                    showErrorToast(response.data.message || 'Upload failed');
                    setUploading(false);
                    setUploadPhase('');
                }
            }
        } catch (error) {
            console.error('Error uploading captions:', error);
            showErrorToast(error.response?.data?.message || error.message || 'Failed to upload captions');
            setUploading(false);
            setUploadPhase('');
            setUploadProgress(0);
        }
    };

    const startPolling = (jobId) => {
        const interval = setInterval(async () => {
            try {
                const statusRes = await axios.get(`/api/locations/bulk-upload-status/${jobId}`);
                
                if (statusRes.data.success) {
                    const job = statusRes.data.data;
                    
                    setUploadProgress(job.progress);
                    
                    if (job.status === 'completed') {
                        clearInterval(interval);
                        setPollingInterval(null);
                        setUploadResult(job.results);
                        setUploading(false);
                        setUploadPhase('');
                        showMsgSuccessToast('Caption files uploaded successfully!');
                        fetchLocations();
                        
                        // Clear file selection
                        setSelectedFile(null);
                        const inputElement = document.getElementById('caption-zip-input');
                        if (inputElement) inputElement.value = '';
                    } else if (job.status === 'failed') {
                        clearInterval(interval);
                        setPollingInterval(null);
                        setUploading(false);
                        setUploadPhase('');
                        showErrorToast(job.error || 'Upload failed');
                    }
                }
            } catch (pollErr) {
                console.error('Error polling job status:', pollErr);
            }
        }, 2000); // Poll every 2 seconds

        setPollingInterval(interval);

        // Safety timeout after 30 minutes
        setTimeout(() => {
            clearInterval(interval);
            if (uploading) {
                setUploading(false);
                setUploadPhase('');
                showErrorToast('Upload timed out. Please check the results.');
            }
        }, 30 * 60 * 1000);
    };

    const getCaptionStatus = (location) => {
        const customCaption = location.avatarVideo?.captionFile;
        const generatedCaptions = location.avatarVideosMultiLang;

        let customStatus = customCaption ? '✓' : '✗';
        let generatedVideos = [];

        if (generatedCaptions && typeof generatedCaptions === 'object') {
            const entries = generatedCaptions instanceof Map
                ? Array.from(generatedCaptions.entries())
                : Object.entries(generatedCaptions);

            entries.forEach(([language, video]) => {
                if (video && typeof video === 'object' && video.videoId) {
                    generatedVideos.push({
                        language: String(language),
                        videoId: String(video.videoId),
                        hasCaption: !!video.captionFile,
                    });
                }
            });
        }

        return { customStatus, generatedVideos };
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Bulk Caption Upload
            </Typography>

            {!userId && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    User ID is missing. Please navigate to this page from the Wayfinder menu.
                </Alert>
            )}

            {/* Upload Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Upload Caption ZIP File
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            ZIP File Structure:
                        </Typography>
                        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {`captions.zip

  ├── <videoId>.vtt
  ├── <videoId>.srt`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            • SRT files will be automatically converted to VTT
                            <br />
                            • Supports any file size — large files are streamed
                            <br />
                            • For custom: Use MappedIn External ID in filename
                            <br />
                            • For generated: Use video ID (found in table below)
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            id="caption-zip-input"
                            type="file"
                            accept=".zip"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="caption-zip-input">
                            <Button
                                variant="outlined"
                                component="span"
                                disabled={uploading}
                            >
                                Select ZIP File
                            </Button>
                        </label>

                        {selectedFile && (
                            <Chip
                                label={`${selectedFile.name} (${formatSize(selectedFile.size)})`}
                                onDelete={() => {
                                    setSelectedFile(null);
                                    const inputElement = document.getElementById('caption-zip-input');
                                    if (inputElement) inputElement.value = '';
                                }}
                                color="primary"
                            />
                        )}

                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                            sx={{
                                backgroundColor: buttonColor,
                                '&:hover': {
                                    backgroundColor: buttonColor,
                                    opacity: 0.9,
                                },
                            }}
                        >
                            {uploading ? 'Processing...' : 'Upload'}
                        </Button>
                    </Box>

                    {uploading && (
                        <Box sx={{ mt: 2 }}>
                            {uploadPhase === 'uploading' && (
                                <>
                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                        📤 Uploading to server... {uploadProgress}%
                                    </Typography>
                                </>
                            )}
                            {uploadPhase === 'processing' && (
                                <>
                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        Processing captions in background... {uploadProgress}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}>
                                        You can close this page - the upload will continue in the background
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Upload Results */}
                {uploadResult && (
                    <Box sx={{ mt: 3 }}>
                        <Alert severity={uploadResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Upload Complete! {uploadResult.successful} of {uploadResult.totalProcessed} captions processed successfully.
                            </Typography>
                        </Alert>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                            <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                                <Typography variant="body2" color="text.secondary">Total Processed</Typography>
                                <Typography variant="h4">{uploadResult.totalProcessed || 0}</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                                <Typography variant="body2" color="text.secondary">Successful</Typography>
                                <Typography variant="h4" color="success.main">{uploadResult.successful || 0}</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
                                <Typography variant="body2" color="text.secondary">Failed</Typography>
                                <Typography variant="h4" color="error.main">{uploadResult.failed || 0}</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                                <Typography variant="body2" color="text.secondary">Skipped</Typography>
                                <Typography variant="h4" color="warning.main">{uploadResult.skipped || 0}</Typography>
                            </Paper>
                        </Box>

                        {uploadResult.details && uploadResult.details.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Details:</Typography>
                                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    {uploadResult.details.map((detail, index) => (
                                        <Alert
                                            key={index}
                                            severity={detail.status === 'success' ? 'success' : detail.status === 'error' ? 'error' : 'warning'}
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography variant="body2">
                                                <strong>{detail.filename}</strong>: {detail.message}
                                            </Typography>
                                        </Alert>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>

            {/* Locations Table */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Locations & Video IDs
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Location Name</strong></TableCell>
                                    <TableCell><strong>MappedIn External ID</strong></TableCell>
                                    <TableCell align="center"><strong>Custom Caption</strong></TableCell>
                                    <TableCell><strong>Generated Videos (Language | Video ID | Caption Status)</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locations.map((location) => {
                                    const { customStatus, generatedVideos } = getCaptionStatus(location);
                                    return (
                                        <TableRow key={location._id}>
                                            <TableCell>{location.name[currentLanguage] || location.name['en']}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                                    {location.externalId || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={customStatus}
                                                    size="small"
                                                    color={customStatus === '✓' ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {generatedVideos.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {generatedVideos.map((video) => (
                                                            <Box
                                                                key={video.videoId}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    bgcolor: video.hasCaption ? '#e8f5e9' : '#f5f5f5',
                                                                    border: '1px solid',
                                                                    borderColor: video.hasCaption ? '#4caf50' : '#e0e0e0'
                                                                }}
                                                            >
                                                                <Chip
                                                                    label={video.language.toUpperCase()}
                                                                    size="small"
                                                                    color={video.hasCaption ? 'success' : 'default'}
                                                                    sx={{ minWidth: 50, fontWeight: 600 }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ fontFamily: 'monospace', fontSize: '12px', flex: 1 }}
                                                                >
                                                                    {video.videoId}
                                                                </Typography>
                                                                <Chip
                                                                    label={video.hasCaption ? '✓ Caption' : '✗ No Caption'}
                                                                    size="small"
                                                                    color={video.hasCaption ? 'success' : 'default'}
                                                                    variant={video.hasCaption ? 'filled' : 'outlined'}
                                                                    sx={{ fontSize: '11px' }}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No generated videos
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default BulkCaptionUpload;
